import React, { useEffect, useState } from 'react'
import AppDialog from '@/shared/components/ui/AppDialog'
import { useTenants } from '../hooks/useTenants'

/**
 * Optional prefill — consumed by the agent-chat IntentPipeline so the dialog
 * opens with fields hydrated from a user's natural-language request. Kept as
 * `Partial<...>` so the consumer only passes what the extractor resolved.
 * Unknown keys are allowed (forwards-compat for business_type, notes, etc.)
 * but are stored as dialog-local metadata, not submitted until the tenants
 * schema gains native columns for them.
 */
export interface TenantPrefill {
  tenant_name?:   string
  display_name?:  string
  description?:   string
  business_type?: string
  notes?:         string
  [key: string]:  unknown
}

interface Props {
  open: boolean
  onOpenChange: (v: boolean) => void
  /** Optional initial values — when supplied the dialog opens pre-hydrated. */
  prefill?: TenantPrefill
}

export default function CreateTenantDialog({ open, onOpenChange, prefill }: Props) {
  const { create } = useTenants()
  const [name, setName] = useState(prefill?.tenant_name ?? '')

  // Re-hydrate when the dialog opens with a new prefill (e.g. the user sent
  // a second smart-send message and the extractor resolved a different name).
  // We watch `open` so the prefill only applies on the open transition —
  // typing in the field while open is never clobbered.
  useEffect(() => {
    if (open) setName(prefill?.tenant_name ?? '')
  }, [open, prefill?.tenant_name])

  const reset = () => setName('')

  const canCreate = name.trim().length > 0 && !create.isPending

  const handleCreate = async () => {
    if (!canCreate) return
    await create.mutateAsync({ tenant_name: name.trim() })
    reset()
    onOpenChange(false)
  }

  const hasMetadata =
    !!prefill?.display_name || !!prefill?.description
    || !!prefill?.business_type || !!prefill?.notes

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => { if (!v) reset(); onOpenChange(v) }}
      title="New tenant"
      description="Create a new tenant in the workflow schema."
      size="sm"
      onConfirm={handleCreate}
      confirmLabel={create.isPending ? 'Creating…' : 'Create tenant'}
      confirmDisabled={!canCreate}
      isBusy={create.isPending}
    >
      <label className="block text-sm text-neutral-300">
        Tenant name
        <input
          autoFocus
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
          placeholder="e.g. acme-inc"
          className="mt-1 w-full bg-black border border-neutral-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </label>

      {/* Read-only hints from the IntentPipeline prefill. These fields are
          captured by the extractor but do not yet persist to the tenants
          table — shown here so the user knows the assistant understood
          them and can correct the primary fields before submit. */}
      {hasMetadata && (
        <div className="mt-3 space-y-1 rounded border border-white/[0.06] bg-white/[0.02] px-3 py-2 text-xs text-neutral-400">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500">Extracted context</div>
          {prefill?.display_name && <Row label="Display name" value={prefill.display_name} />}
          {prefill?.business_type && <Row label="Business type" value={prefill.business_type} />}
          {prefill?.description   && <Row label="Description"   value={prefill.description} />}
          {prefill?.notes         && <Row label="Notes"         value={prefill.notes} />}
        </div>
      )}

      {create.isError && (
        <p className="text-sm text-rose-400">
          {(create.error as Error)?.message ?? 'Could not create tenant.'}
        </p>
      )}
    </AppDialog>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[10px] uppercase tracking-wide text-neutral-500 w-28 shrink-0">{label}</span>
      <span className="text-neutral-300 truncate">{value}</span>
    </div>
  )
}
